export interface SessionData {
  goal: string;
  duration: number;
  customDuration: string;
  breakReminders: boolean;
  music: string;
  customMusicURL?: string;
  roomName: string;
  roomCode: string;
  projectId?: string; // Optional project ID
  notes?: string[];
  tasks?: Task[];
}

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
}

// Focus Session Enums
export enum FocusSessionType {
  SOLO = 'solo',
  TEAM = 'team',
}

export enum FocusSessionStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum FocusMusicType {
  SILENCE = 'silence',
  NOISE = 'noise',
  NATURE = 'nature',
  BINAURAL = 'binaural',
  CUSTOM = 'custom',
}

export enum AnalyticsType {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

// Analytics Data Interface
export interface AnalyticsData {
  weekly?: {
    days: Array<{ day: string; hours: number; sessions: number; date: string }>;
    average: number;
    streak: number;
  };
  monthly?: {
    calendar: Array<{ date: number; hours: number; sessions: number; isToday: boolean }>;
    totalHours: number;
  };
  yearly?: {
    months: Array<{ month: string; hours: number; sessions: number; isPeak: boolean }>;
    totalHours: number;
  };
  // Project-wise breakdown (optional)
  projects?: Array<{
    projectId: string;
    projectName: string;
    totalHours: number;
    sessionCount: number;
  }>;
}

// Focus Session DTOs
export interface CreateFocusSessionDTO {
  goal: string;
  duration: number; // in minutes
  breakReminders: boolean;
  music: FocusMusicType;
  customMusicURL?: string; // YouTube URL when music is CUSTOM
  startTime: string; // ISO string
  roomName?: string;
  roomCode?: string;
  projectId?: string; // Optional project ID
  tasks?: Task[];
}

export interface UpdateFocusSessionDTO {
  goal?: string;
  duration?: number;
  breakReminders?: boolean;
  music?: FocusMusicType;
  customMusicURL?: string; // YouTube URL when music is CUSTOM
  notes?: string[];
  endTime?: Date;
  completed?: boolean;
  projectId?: string; // Optional project ID
  tasks?: Task[];
}

// Paginated API Response
export interface PaginatedResponse<T> {
  total: number;
  limit: number;
  offset: number;
  data: T[];
}

// API Response Focus Session (matches backend response)
export interface ApiFocusSession {
  id: string;
  createdBy: string;
  duration: number;
  completed: boolean;
  type: string; // "focus", "break", "long-break"
  goal: string;
  notes: string[];
  tasks?: Task[];
  tags: string[];
  session_type: string; // "solo", "team"
  music: string; // "silence", "noise", "nature", "binaural", "custom"
  customMusicURL?: string; // YouTube URL when music is "custom"
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  breakReminders?: boolean;
  roomId?: string;
  xp?: number;
  members?: string[];
  startedAt?: string;
  completedAt?: string;
  projectId?: string; // Optional project ID
}

export interface FocusSession {
  id: string;
  createdBy: string;
  goal: string;
  duration: number; // in minutes
  type: FocusSessionType;
  status: FocusSessionStatus;
  breakReminders: boolean;
  music: FocusMusicType;
  customMusicURL?: string; // YouTube URL when music is CUSTOM
  rtcToken?: string;
  roomId?: string;
  notes?: string | string[];
  tasks?: Task[];
  tags?: string[];
  xp?: number;
  members?: string[]; // For team sessions
  projectId?: string; // Optional project ID
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  startedAt?: string; // ISO string
  completedAt?: string; // ISO string
}

export interface Task {
  id: string; // Helper ID for UI
  text?: string;
  title?: string;
  description?: string;
  status?: 'todo' | 'inprogress' | 'done';
  completed?: boolean;
  createdAt: Date | string;
  created_by?: { id: string; name: string } | string;
  assigned_to?: { id: string; name: string } | string | null;
  assignedTo?: { id: string; name: string; avatar: string } | null;
  is_public?: boolean;
}

// Mapper function to convert API response to FocusSession
export const mapApiFocusSessionToFocusSession = (apiSession: ApiFocusSession): FocusSession => {
  // Map session_type to FocusSessionType
  const sessionType = apiSession.session_type === 'team'
    ? FocusSessionType.TEAM
    : FocusSessionType.SOLO;

  // Map completed to status
  const status = apiSession.completed
    ? FocusSessionStatus.COMPLETED
    : FocusSessionStatus.PENDING;

  // Map music string to FocusMusicType enum
  const music = apiSession.music as FocusMusicType;

  // Convert notes array to string if needed (or keep as array)
  const notes = apiSession.notes && apiSession.notes.length > 0
    ? apiSession.notes
    : undefined;

  return {
    id: apiSession.id,
    createdBy: apiSession.createdBy,
    goal: apiSession.goal,
    duration: apiSession.duration,
    type: sessionType,
    status: status,
    breakReminders: apiSession.breakReminders ?? false,
    music: music,
    customMusicURL: apiSession.customMusicURL,
    roomId: apiSession.roomId,
    notes: notes,
    tasks: apiSession.tasks,
    tags: apiSession.tags,
    xp: apiSession.xp,
    members: apiSession.members,
    projectId: apiSession.projectId,
    createdAt: apiSession.createdAt,
    updatedAt: apiSession.updatedAt,
    startedAt: apiSession.startedAt,
    completedAt: apiSession.completedAt,
  };
};

// Focus Session Stats
export interface FocusStats {
  totalHours: number;
  avgHoursPerSession: number;
  totalSessions: number;
  streak: number;
  productivity: number;
}

// Game Stats
export interface GameStats {
  gamesPlayed: number;
  totalTime: number;
  badgesEarned: number;
}

// Game Analytics
export interface GameAnalytics {
  totalGames: number;
  totalDuration: number;
  avgDuration: number;
  avgScore: number;
}

// Create Game DTO
export interface CreateGameDTO {
  gameId: string;
  gameName: string;
  category: string;
  emoji: string;
  description: string;
  duration: number;
  score: number;
  completed: boolean;
}

// Change Password Request
export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// Project Types
export interface Project {
  id: string;
  createdBy: string;
  name: string;
  description?: string;
  color: string; // Hex color code for visual identification (required)
  isDefault: boolean;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
}export interface CreateProjectDTO {
  name: string;
  description?: string;
  color: string; // Required field
}

export interface UpdateProjectDTO {
  name?: string;
  description?: string;
  color?: string;
}